// React
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import React from "react"

// Hooks and Redux
import { useAppSelector } from "../../../redux/hooks"

// MUI Components
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Icon from "@mui/material/Icon"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"

import {
  GridCellParams,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  DataGrid,
  enUS,
} from "@mui/x-data-grid"
// Utils
import { useTranslation } from "react-i18next"

// Types
import theme from "../../../styles/theme"
import { thTHGrid } from "../../../@types/DataGridLocaleText"

// Services
import { getAllActiveCountryNationalID } from "../../../services/MasterDataService"

const CountryOfNationalId = () => {
  const { t, i18n } = useTranslation()
  const userRoleType = useAppSelector((state) => state.user.roletype)
  const navigate = useNavigate()

  const [loading, setLoading] = useState<boolean>(true)
  const [rows, setRows] = useState<any[]>([])

  const allSuccessLoading = !loading

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await getAllActiveCountryNationalID('All')
        const data = await response.data
        if (data.success) {
          setRows(data.countriesOfNationalId)
        } else {
          setRows([])
        }
      } catch (error) {
        navigate("/servererror", { replace: true })
      } finally {
        setTimeout(() => setLoading(false), 500)
      }
    }
    fetchData()
  }, [navigate])

  // NOTE: CustomToolbar — ซ้ำกันทุกหน้าที่มี DataGrid (37+ ไฟล์) ควรย้ายไปรวมใน components/common/DataGridToolbar.tsx
  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </GridToolbarContainer>
    )
  }

  const columns: GridColDef[] = [
    {
      field: "edit",
      headerName: `${t("Edit")}`,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      width: 80,
      renderCell: (params) => {
        if (userRoleType === "Super Admin") {
          return (
            <Link
              to={`/admin/masterdata/country-national/edit/${params.row.country_code}`}
              state={{ countryData: params.row }}
              style={{ display: "flex", alignItems: "center", cursor: "pointer", color: theme.palette.button?.main ?? "#0288d1" }}
            >
              <Tooltip title={t("Edit")}>
                <Icon fontSize="medium">edit</Icon>
              </Tooltip>
            </Link>
          )
        }
        return null
      },
    },
    {
      field: "is_active",
      headerName: `${t("Status")}`,
      width: 120,
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) =>
        params.row.is_active === true ? (
          <Chip variant="outlined" color="success" label={`${t("Active")}`} size="small" />
        ) : (
          <Chip variant="outlined" color="error" label={`${t("Inactive")}`} size="small" />
        ),
    },
    { field: "country_code", headerName: `${t("Country Code")}`, width: 150, valueGetter: (params: GridCellParams) => params.row.country_code },
    { field: "country_name", headerName: `${t("Country Name")}`, width: 300, valueGetter: (params: GridCellParams) => params.row.country_name },
  ]

  return (
    <div>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "70px" }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", textAlign: "left" }}>
          {t("MasterData Country of National ID")}
        </Typography>
        {(userRoleType === "Super Admin") && (
          <Button
            variant="contained"
            disableElevation
            sx={{
              backgroundColor: theme.palette.button?.main ?? "#0288d1",
              color: "white",
              "&:hover": { backgroundColor: "#0277bd" },
              minWidth: "fit-content",
              minHeight: "42px",
            }}
            component={Link}
            to="/admin/masterdata/country-national/create"
          >
            {t("Create New Country of National ID")}
          </Button>
        )}
      </Box>

      <Box sx={{ width: "100%", paddingX: 3, mt: 3 }}>
        {!allSuccessLoading ? (
          <Box display="flex" justifyContent="center" gap={2}>
            <CircularProgress color="success" size={20} />
            <Typography align="center">{t("Loading")}...</Typography>
          </Box>
        ) : rows.length !== 0 ? (
          <Box display="inline-grid" sx={{ width: "100%" }}>
            <DataGrid
              getRowId={(row) => row.country_code}
              rows={rows}
              columns={columns}
              initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
              pagination
              pageSizeOptions={[10, 20, 30, 40, 50]}
              getRowHeight={() => 50}
              disableRowSelectionOnClick
              sx={{
                "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus": { outline: "none !important" },
                fontSize: 13,
              }}
              slots={{ toolbar: CustomToolbar }}
              localeText={i18n.language === "en" ? enUS.components.MuiDataGrid.defaultProps.localeText : thTHGrid}
            />
          </Box>
        ) : (
          <Typography align="center">{t("No data")}</Typography>
        )}
      </Box>
    </div>
  )
}

export default CountryOfNationalId
